import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { useRef, useState } from 'react';
import PartnerHeader from './PartnerHeader';
import Tabs from './Tabs';
import MarketTable from './MarketTable';
import GamesTable from './GamesTable';
import ContactsTab from './ContactsTab';
import AddMarketModal from './AddMarketModal';
import { useParams } from 'react-router';
import Breadcrumbs from './Breadcrumbs';
import { useQuery } from '@tanstack/react-query';
import {
  fetchGames,
  fetchPartnersMarkets,
  fetchPartners,
} from '@/lib/api';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { mapFiltersToParams } from '../Resources/ResourcePageHelpers';
import { GameSchema, PartnerSchema } from '@/lib/types';
import { Order } from '@/components/Table/TableHelpers';
import { useNotFoundRedirect } from '@/hooks/useNotFoundRedirect';

const AdminPage = () => {
  const [selectedTab, setSelectedTab] = useState('markets');
  const [addMarketModalOpen, setAddMarketModalOpen] = useState(false);
  const [gamesSearchText, setGamesSearchText] = useState('');
  const [gamesOrder, setGamesOrder] = useState<Order>('asc');
  const [gamesOrderBy, setGamesOrderBy] =
    useState<keyof GameSchema>('game_name');
  const [gamesFilters, setGamesFilters] = useState<
    Partial<FilterSchema>
  >({});
  const params = useParams();

  const partnerQuery = useQuery<PartnerSchema[]>({
    queryKey: ['partners', params.partnerId],
    queryFn: () => {
      if (!params.partnerId) {
        return Promise.resolve([]);
      }
      return fetchPartners({
        partner_ids: [Number(params.partnerId)],
      });
    },
    enabled: !!params.partnerId,
    retry: false,
  });

  useNotFoundRedirect({
    queryResult: partnerQuery,
    checkResourceExists: (data) =>
      !!data.find((p) => p.id === Number(params.partnerId)),
  });

  const {
    data: marketsData,
    isPending: isMarketsPending,
    refetch,
  } = useQuery({
    queryKey: ['partners_markets', params.partnerId],
    queryFn: () => {
      if (!params.partnerId) {
        return Promise.resolve([]);
      } else {
        return fetchPartnersMarkets(params.partnerId);
      }
    },
    enabled: !!params.partnerId,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gamesParams = {
    partner_ids: [params.partnerId] as string[],
    game_name: gamesSearchText,
    sort_by: gamesOrderBy,
    sort_order: gamesOrder,
    ...mapFiltersToParams(gamesFilters),
  };
  const { data: gamesData, isFetching: isGamesPending } =
    useInfiniteScroll({
      fetchFn: (params: typeof gamesParams) => {
        if (!params.partner_ids) {
          return Promise.resolve({ games: [], total_count: 0 });
        } else {
          return fetchGames(params);
        }
      },
      params: gamesParams,
      queryKey: ['games', ...Object.values(gamesParams)],
      scrollContainerRef:
        scrollContainerRef as React.RefObject<HTMLElement>,
      pageSize: 10,
      initialLimit: 10,
      scrollThreshold: 200,
      extractData: (response) => response.games,
    });

  const handleMarketDetach = () => {
    refetch();
  };

  const handleAddMarketModalClose = () => {
    setAddMarketModalOpen(false);
    refetch();
  };

  const onGamesRequestSort = (property: keyof GameSchema) => {
    setGamesOrderBy(property);
    setGamesOrder(gamesOrder === 'asc' ? 'desc' : 'asc');
  };

  return (
    <Root ref={scrollContainerRef} className="wide-scrollbar">
      <div className="page-width-wrapper">
        <Breadcrumbs />
        {params.partnerId && (
          <>
            <PartnerHeader partnerId={params.partnerId} />
            <StyledTabs
              value={selectedTab}
              onChange={setSelectedTab}
              options={[
                {
                  id: 'markets',
                  label: (
                    <FormattedMessage
                      id="markets"
                      defaultMessage="Markets"
                    />
                  ),
                },
                {
                  id: 'games',
                  label: (
                    <FormattedMessage
                      id="games"
                      defaultMessage="Games"
                    />
                  ),
                },
                {
                  id: 'contacts',
                  label: (
                    <FormattedMessage
                      id="contacts"
                      defaultMessage="Contacts"
                    />
                  ),
                },
              ]}
            />
            {selectedTab === 'markets' ? (
              <MarketTable
                loading={isMarketsPending}
                onAddMarketClick={() => setAddMarketModalOpen(true)}
                selectedPartnerId={params.partnerId}
                markets={marketsData || []}
                onMarketDetach={handleMarketDetach}
              />
            ) : null}
            {selectedTab === 'games' ? (
              <GamesTable
                loading={isGamesPending}
                games={gamesData || []}
                searchText={gamesSearchText}
                onSearchTextChange={setGamesSearchText}
                filters={gamesFilters}
                onFiltersChange={setGamesFilters}
                order={gamesOrder}
                orderBy={gamesOrderBy}
                onRequestSort={onGamesRequestSort}
              />
            ) : null}
            {selectedTab === 'contacts' ? (
              <ContactsTab partnerId={params.partnerId} />
            ) : null}
          </>
        )}
        <AddMarketModal
          partnerId={params.partnerId}
          open={addMarketModalOpen}
          onClose={handleAddMarketModalClose}
          partnerMarkets={marketsData || []}
        />
      </div>
    </Root>
  );
};

const Root = styled('div')({
  paddingBottom: '30px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
});

const StyledTabs = styled(Tabs)({
  margin: '0px 40px',
});

export default AdminPage;
